import React, { useState, useMemo } from 'react';
import { Transaction, RepairJob, JobStatus, Language } from '../types';
import { Plus, TrendingUp, TrendingDown, Banknote, ArrowUpRight, ArrowDownRight, X, Layers, Calendar, FileText, Tag, Save, Loader2 } from 'lucide-react';
import { formatRiel } from '../constants';
import { useTranslation } from '../LanguageContext';
import { toast } from 'sonner';

interface ShopFinanceProps {
  jobs: RepairJob[];
  transactions: Transaction[]; 
  shopId: string;
  onAddTransaction: (t: Transaction) => Promise<void> | void;
}

export const ShopFinance: React.FC<ShopFinanceProps> = ({ jobs, transactions, shopId, onAddTransaction }) => {
  const { language } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Parts');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'INCOME' | 'EXPENSE'>('EXPENSE');

  const texts: Record<string, Record<Language, string>> = {
    revenue: { EN: 'Total Revenue', KH: 'ចំណូលសរុប', ZH: '总收入', JA: '総収益', KO: '총 수익' },
    expenses: { EN: 'Total Expenses', KH: 'ចំណាយសរុប', ZH: '总支出', JA: '総支出', KO: '총 지출' },
    profit: { EN: 'Net Profit', KH: 'ប្រាក់ចំណេញសុទ្ធ', ZH: '净利润', JA: '純利益', KO: '순이익' },
    transactions: { EN: 'Transactions', KH: 'ប្រតិបត្តិការហិរញ្ញវត្ថុ', ZH: '交易记录', JA: '取引履歴', KO: '거래 내역' },
    add_record: { EN: 'Add Record', KH: 'បន្ថែមទិន្នន័យ', ZH: '添加记录', JA: '記録を追加', KO: '기록 추가' },
    income: { EN: 'Income', KH: 'ចំណូល', ZH: '收入', JA: '收入', KO: '수입' },
    expense: { EN: 'Expense', KH: 'ចំណាយ', ZH: '支出', JA: '支出', KO: '지출' },
    save: { EN: 'Save Record', KH: 'រក្សាទុកទិន្នន័យ', ZH: '保存记录', JA: '記録を保存', KO: '기록 저장' },
    saving: { EN: 'Saving...', KH: 'កំពុងរក្សាទុក...', ZH: '保存中...', JA: '保存中...', KO: '저장 중...' },
    type: { EN: 'Type', KH: 'ប្រភេទ', ZH: '类型', JA: 'タイプ', KO: '유형' },
    cat: { EN: 'Category', KH: 'ប្រភេទចំណាយ', ZH: '类别', JA: 'カテゴリ', KO: '카테고리' },
    amt: { EN: 'Amount', KH: 'ចំនួនទឹកប្រាក់', ZH: '金额', JA: '金額', KO: '금액' },
    desc: { EN: 'Description', KH: 'ការរៀបរាប់', ZH: '描述', JA: '説明', KO: '说明' }
  };

  const allTransactions = useMemo(() => {
    const jobTransactions: Transaction[] = jobs
      .filter(j => j.status === JobStatus.COMPLETED && j.invoice) 
      .map(j => ({
        id: `job-rev-${j.id}`,
        shopId: j.shopId,
        type: 'INCOME',
        category: 'Service Revenue',
        amount: j.invoice?.totalAmount || 0,
        description: `Revenue: ${j.customerName}`,
        date: j.invoice?.createdAt || j.createdAt
      }));
    return [...jobTransactions, ...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [jobs, transactions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmt = parseFloat(amount);
    if (isNaN(numAmt) || numAmt <= 0 || !description.trim()) return;
    
    setIsSaving(true);
    try {
      await onAddTransaction({
        id: `trx-${Date.now()}`,
        shopId: shopId,
        type,
        category,
        amount: Math.round(numAmt),
        description: description.trim(),
        date: new Date().toISOString()
      });
      
      setIsAdding(false);
      setAmount('');
      setDescription('');
    } catch (err) {
      console.error("Failed to add transaction", err);
      toast.error("Failed to save transaction. Please check your network.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-300">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group">
            <div className="relative z-10">
               <div className="flex items-center space-x-2 text-emerald-600 mb-3">
                  <div className="p-2.5 bg-emerald-100 rounded-xl"><TrendingUp size={18} /></div>
                  <span className="font-black text-[10px] uppercase tracking-widest">{texts.revenue[language]}</span>
               </div>
               <h3 className="text-3xl font-black text-slate-900 tracking-tighter">{formatRiel(allTransactions.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.amount, 0))}</h3>
            </div>
         </div>
         <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 relative overflow-hidden group">
            <div className="relative z-10">
               <div className="flex items-center space-x-2 text-red-600 mb-3">
                  <div className="p-2.5 bg-red-100 rounded-xl"><TrendingDown size={18} /></div>
                  <span className="font-black text-[10px] uppercase tracking-widest">{texts.expenses[language]}</span>
               </div>
               <h3 className="text-3xl font-black text-slate-900 tracking-tighter">{formatRiel(allTransactions.filter(t => t.type === 'EXPENSE').reduce((s, t) => s + t.amount, 0))}</h3>
            </div>
         </div>
         <div className="bg-slate-900 rounded-[2rem] p-8 shadow-xl text-white relative overflow-hidden group">
            <div className="relative z-10">
               <div className="flex items-center space-x-2 text-slate-300 mb-3">
                  <div className="p-2.5 bg-white/10 rounded-xl"><Banknote size={18} /></div>
                  <span className="font-black text-[10px] uppercase tracking-widest">{texts.profit[language]}</span>
               </div>
               <h3 className="text-3xl font-black mb-1.5 tracking-tighter">
                  {formatRiel(
                     allTransactions.filter(t => t.type === 'INCOME').reduce((s, t) => s + t.amount, 0) - 
                     allTransactions.filter(t => t.type === 'EXPENSE').reduce((s, t) => s + t.amount, 0)
                  )}
               </h3>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
           <h3 className="text-xl font-black text-slate-900 tracking-tight">{texts.transactions[language]}</h3>
           <button 
             onClick={() => setIsAdding(!isAdding)} 
             className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center shadow-lg active:scale-95 ${isAdding ? 'bg-slate-100 text-slate-600' : 'bg-slate-900 text-white hover:bg-violet-600'}`}
           >
              {isAdding ? <X size={16} className="mr-2"/> : <Plus size={16} className="mr-2"/>} 
              {isAdding ? 'Cancel' : texts.add_record[language]}
           </button>
        </div>

        {isAdding && (
          <div className="p-8 bg-slate-50 border-b border-slate-100 animate-in slide-in-from-top-4 duration-300">
             <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
                <div className="space-y-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {texts.type[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <select value={type} onChange={(e) => setType(e.target.value as any)} className="w-full p-4 rounded-xl border border-slate-200 uppercase text-[10px] font-black bg-white outline-none focus:ring-4 focus:ring-violet-100">
                      <option value="EXPENSE">{texts.expense[language]}</option>
                      <option value="INCOME">{texts.income[language]}</option>
                   </select>
                </div>
                <div className="space-y-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {texts.cat[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input type="text" placeholder="e.g. Parts" value={category} onChange={e => setCategory(e.target.value)} className="w-full p-4 rounded-xl border border-slate-200 text-sm font-bold bg-white outline-none focus:ring-4 focus:ring-violet-100" />
                </div>
                <div className="space-y-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {texts.amt[language]} (៛) <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input type="number" placeholder="0" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-4 rounded-xl border border-slate-200 text-sm font-bold bg-white outline-none focus:ring-4 focus:ring-violet-100" />
                </div>
                <div className="space-y-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {texts.desc[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input type="text" placeholder="Detail..." value={description} onChange={e => setDescription(e.target.value)} className="w-full p-4 rounded-xl border border-slate-200 text-sm font-bold bg-white outline-none focus:ring-4 focus:ring-violet-100" />
                </div>
                <button type="submit" disabled={isSaving} className="py-4 bg-emerald-600 text-white rounded-xl font-black text-[10px] uppercase shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                   {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} 
                   {isSaving ? texts.saving[language] : texts.save[language]}
                </button>
             </form>
          </div>
        )}

        <div className="divide-y divide-slate-50">
          {allTransactions.length === 0 ? (
             <div className="py-20 text-center opacity-20 flex flex-col items-center">
                <Banknote size={48} className="mb-4" />
                <p className="font-black uppercase text-xs tracking-widest">No financial data yet</p>
             </div>
          ) : (
            allTransactions.map((t) => (
               <div key={t.id} className="p-6 md:p-8 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                  <div className="flex items-center gap-6">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-transform group-hover:scale-105 ${t.type === 'INCOME' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                        {t.type === 'INCOME' ? <ArrowDownRight size={24}/> : <ArrowUpRight size={24}/>}
                     </div>
                     <div>
                        <h4 className="font-black text-slate-900 text-lg leading-tight mb-1">{t.description}</h4>
                        <div className="flex items-center gap-2">
                           <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-md">{t.category}</span>
                           <span className="text-[9px] font-bold text-slate-300">•</span>
                           <span className="text-[9px] font-bold text-slate-400 uppercase">{new Date(t.date).toLocaleDateString()}</span>
                        </div>
                     </div>
                  </div>
                  <div className={`text-right font-black text-xl tracking-tighter ${t.type === 'INCOME' ? 'text-emerald-600' : 'text-slate-900'}`}>
                     {t.type === 'INCOME' ? '+' : '-'}{formatRiel(t.amount)}
                  </div>
               </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};